import {Box, Button, Card, CardContent, Divider, Grid, Typography} from "@mui/material";
import {useTranslation} from "react-i18next";
import {formatDateWithoutTime} from '../../../utils/FormatUtils';
import InvoiceItems, {ModifiedItem} from "./InvoiceItems";
import {ID_CREDIT_NOTE, ID_DEBIT_NOTE, ID_INVOICE} from "../constants/InvoicesTypes";
import {useInvoiceRepository} from "../../../api/repositories/InvoiceRepository";
import InvoiceTaxes from "./InvoiceTaxes";
import DownloadRoundedIcon from "@mui/icons-material/Download";

import {useNavigate} from "react-router-dom";
import {
    ID_ACCOUNTED, ID_CANCELLED,
    ID_OPEN,
    ID_SENT,
    ID_SUB_OOFC_UNPAID,
    ID_TO_SEND,
    ID_UNACCOUNTED
} from "../constants/InvoicesStatuses";
import {useHasScope} from "../../../../../config/globalConfig";
import {
    CREATE_RECEIVABLE_VARIATION_NOTE,
    DOWNLOAD_RECEIVABLE_PDF,
    DOWNLOAD_RECEIVABLE_XML,
    SUB_EXTRACANONE
} from "../../../utils/FinanceScope";
import dayjs from "dayjs";
import {setSnackbar} from "../../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../../util/state/globalVariables";
import {useContext, useState} from "react";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

export interface InvoiceDetailsProps {
    type?: string,
    invoice: any,
    invoiceParent?: any,
    originalInvoice?: any,
    preview: boolean,
    editable: boolean,
    modifiedItems?: ModifiedItem[],
    setModifiedItems?: any,
    setError?: any,
    setSuccess?: any,
    debitNoteAssistedValues?: any,
    onRefresh?: () => void
}

const InvoiceDetails = (props: InvoiceDetailsProps) => {

    const {t} = useTranslation("finance", {keyPrefix: 'invoices'});
    const hasScope = useHasScope;
    const navigate = useNavigate();
    const {dispatchGlobalData} = useContext(GlobalContext);
    const {downloadPDF, downloadXML, updateStatus} = useInvoiceRepository();

    const type = props.type ? props.type : props.invoice.documentType;
    const status = props.invoice.status === ID_OPEN;
    const VALUES_CREATED_BY = ['anonymousUser', 'Unknown'];
    const [showStatusesExtraCanoneChange, setShowStatusesExtraCanoneChange] =
        useState(!props.editable && props.invoice && type === ID_INVOICE && props.invoice.status === ID_SUB_OOFC_UNPAID);

    function showNoteHeader() {
        if (type !== ID_INVOICE) {
            return (
                <Grid item md={6}>
                    <Typography variant={'h6'} color={'primary'} fontWeight={700}>
                        {t(status ? "preview.header.RECEIPT" : `preview.header.${type}`)}
                        <br/>
                    </Typography>
                    <Typography variant={'body1'} color={'primary'} fontWeight={700}>
                        {t(status ? "preview.subheader_receipt" : "preview.subheader", {
                            invoiceNumber: props.originalInvoice?.sapInvoiceNumber ||
                                props.invoiceParent?.sapInvoiceNumber ||
                                props.invoice?.parent?.sapInvoiceNumber ||
                                props.originalInvoice?.invoiceNumber ||
                                props.invoiceParent?.invoiceNumber ||
                                props.invoice?.parent?.invoiceNumber ||
                                '---',
                            invoiceDate: dayjs(props.invoiceParent?.invoiceDate || props.invoice.invoiceDate).format("L")
                        })}
                    </Typography>
                </Grid>
            );
        }
    }

    function updateInvoiceStatus(invoiceId: string, newStatus: string) {
        updateStatus(invoiceId, newStatus).then(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.success-change-state'),
                        type: snackbarTypes.success,
                        show: true,
                        autoClose: true
                    }
                }
            });
            setShowStatusesExtraCanoneChange(false);

            props.invoice.status = newStatus;

            if (props.onRefresh) {
                props.onRefresh();
            }
        }).catch(() => {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('snackbar.error-change-state'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true
                    }
                }
            });
        });
    }

    return (
        <>
            <Card variant="outlined" sx={{margin: '15px'}}>
                <CardContent>
                    <Grid container rowSpacing={4}>
                        <Grid item container columnSpacing={20} rowSpacing={4} sx={{paddingLeft: "16px"}}>
                            {showNoteHeader()}
                            <Grid item md={12}>
                                <Typography variant={'body1'} color={'neutral.dark'} fontWeight={700}>
                                    {t(status ? 'preview.sections.RECEIPT' : 'preview.sections.' + type)}
                                </Typography>
                            </Grid>
                            <Grid item md={6}>
                                <Typography fontWeight={600} variant={'body1'}>
                                    {t('preview.sections.customer.title')}
                                </Typography>
                                <Typography fontWeight={400}>
                                    {props.invoice.customer.sapId ? props.invoice.customer.sapId : ''}<br></br>
                                    {props.invoice.customer?.companyName ? props.invoice.customer?.companyName : props.invoice.customer?.firstName + " " + props.invoice.customer?.lastName}<br></br>
                                    {props.invoice.customer?.address + ' ' + props.invoice.customer?.houseNumber}<br></br>
                                    {props.invoice.customer?.zip + " " + props.invoice.customer?.city + " " + props.invoice?.customer?.country}<br></br>
                                    {props.invoice.customer.taxCode ? t("preview.sections.customer.tax-code") + " " + props.invoice.customer.taxCode : ""}<br></br>
                                    {props.invoice.customer.vatCode ? t("preview.sections.customer.vat-number") + " " + props.invoice.customer.vatCode : ""}<br></br>
                                    {props.invoice.customer.siren ? <>
                                            {t("preview.sections.customer.siren") + " " + props.invoice.customer.siren}
                                            <br/>
                                            {t("preview.sections.customer.siret") + " " + props.invoice.customer.siret}
                                        </>
                                        : ""
                                    }<br></br>
                                </Typography>
                            </Grid>
                            <Grid item md={6}>
                                <Typography fontWeight={400}>
                                    <b>{props.invoice.legalEntity === 'drivalia_fr' ? t("preview.sections.customer.email") : t("preview.sections.customer.pec-email")} </b>{props.invoice.customer.pec ? props.invoice.customer.pec : props.invoice.customer.email}<br></br>
                                    {(props.invoice.legalEntity === 'drivalia_it' && props.invoice.customer.sdiCode) && <>
                                        <b>{t("preview.sections.customer.sdi")} </b>{props.invoice.customer.sdiCode}<br></br></>}
                                    {props.invoice.paCigCode && <>
                                        <b>{t("preview.sections.customer.cig")} </b>{props.invoice.paCigCode}<br></br></>}
                                    {props.invoice.paCupCode && <>
                                        <b>{t("preview.sections.customer.cup")} </b>{props.invoice.paCupCode}<br></br></>}
                                    {props.invoice.paOrderCode && <>
                                        <b>{t("preview.sections.customer.orderCode")} </b>{props.invoice.paOrderCode}<br></br></>}

                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid item md={12}>
                            <Divider orientation={"horizontal"} variant={"middle"}/>
                        </Grid>
                        <Grid item container rowSpacing={"32px"} columnSpacing={"15px"} sx={{paddingLeft: "16px"}}>
                            <Grid item md={4}>
                                <Typography fontWeight={600} variant={'body1'}>
                                    {t("preview.sections.number")}
                                </Typography>
                                <Typography fontWeight={400} variant={'body1'}>
                                    {props.invoice?.sapInvoiceNumber ? props.invoice?.sapInvoiceNumber : props.invoice?.invoiceNumber}
                                </Typography>
                            </Grid>
                            <Grid item md={4}>
                                <Typography fontWeight={600} variant={'body1'}>
                                    {t("preview.sections.date")}
                                </Typography>
                                <Typography fontWeight={400} variant={'body1'}>
                                    {formatDateWithoutTime(props.invoice.invoiceDate)}
                                </Typography>
                            </Grid>
                            <Grid item md={4}>
                                <Typography fontWeight={600} variant={'body1'}>
                                    {t("preview.sections.due-date")}
                                </Typography>
                                <Typography fontWeight={400} variant={'body1'}>
                                    {formatDateWithoutTime(props.invoice.dueDate)}
                                </Typography>
                            </Grid>
                            <Grid item md={4}>
                                <Typography fontWeight={600} variant={'body1'}>
                                    {t("preview.sections.payment-method")}
                                </Typography>
                                <Typography fontWeight={400} variant={'body1'}>
                                    {props.invoice.paymentMethod.description ? props.invoice.paymentMethod.description : ''}
                                </Typography>
                            </Grid>
                            <Grid item md={4}>
                                <Typography fontWeight={600} variant={'body1'}>
                                    {t("preview.sections.currency")}
                                </Typography>
                                <Typography fontWeight={400} variant={'body1'}>
                                    {props.invoice.currency ? props.invoice.currency : "Eur"}
                                </Typography>
                            </Grid>
                            <Grid item md={4}>
                                <Typography fontWeight={600} variant={'body1'}>
                                    {t("preview.sections.invoice-schema")}
                                </Typography>
                                <Typography fontWeight={400} variant={'body1'}>
                                    {props.invoice.invoiceSchema?.description}
                                </Typography>
                            </Grid>
                            {(type === ID_CREDIT_NOTE && props.invoice.createdBy && !VALUES_CREATED_BY.includes(props.invoice.createdBy)) &&
                                <Grid item md={4}>
                                    <Typography fontWeight={600} variant={'body1'}>
                                        {t("preview.sections.createdBy")}
                                    </Typography>
                                    <Typography fontWeight={400} variant={'body1'}>
                                        {props.invoice.createdBy}
                                    </Typography>
                                </Grid>
                            }
                        </Grid>
                        <Grid item md={12}>
                            <Divider orientation={"horizontal"} variant={"middle"}/>
                        </Grid>
                        <Grid item container sx={{width: '99%'}}>
                            <InvoiceItems invoice={props.invoice} parentInvoice={props.invoiceParent}
                                          originalInvoice={props.originalInvoice}
                                          editable={props.editable} preview={props.preview} editableType={props.type}
                                          modifiedItems={props.modifiedItems}
                                          debitNoteAssistedValues={props.debitNoteAssistedValues}
                                          setModifiedItems={props.setModifiedItems} setError={props.setError}
                                          setSuccess={props.setSuccess}
                            />
                        </Grid>
                        <Grid item md={12}>
                            <Divider orientation={"horizontal"} variant={"middle"}/>
                        </Grid>
                        <Grid item md={6}>
                            {type === ID_CREDIT_NOTE &&
                                <Typography variant={'body1'} fontWeight={700} sx={{marginBottom: "2ch"}}
                                            color={'accent.main'}>
                                    {t("preview.sections.credit-count")}
                                </Typography>
                            }
                        </Grid>
                        <Grid item md={12}>
                            <InvoiceTaxes invoice={props.invoice} type={props.type}/>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {!props.editable && !props.type && <Box style={{display: 'flex', gap: '1ch', justifyContent: "flex-end"}}>
                {(props.invoice.status == ID_ACCOUNTED || props.invoice.status == ID_UNACCOUNTED || props.invoice.status == ID_SENT) && hasScope(DOWNLOAD_RECEIVABLE_XML) &&
                    <Button variant="outlined" color={'accent'} component="label" endIcon={<DownloadRoundedIcon/>}
                            onClick={() => {
                                downloadXML(props.invoice.id).then((res) => {
                                    if (res?.result) {
                                        const dataURI = 'data:application/xml;charset=utf-8;base64,' + encodeURI(res?.result);
                                        const downloadLink = document.createElement('a');
                                        downloadLink.href = dataURI;
                                        downloadLink.download = "invoice" + props.invoice.id + ".xml";
                                        document.body.appendChild(downloadLink);
                                        downloadLink.click();
                                        document.body.removeChild(downloadLink);
                                    }
                                });
                            }}>
                        {t("preview.buttons.download-xml")}
                    </Button>
                }
                {hasScope(DOWNLOAD_RECEIVABLE_PDF) &&
                    <Button variant="outlined" color={'accent'} component="label" endIcon={<DownloadRoundedIcon/>}
                            onClick={() => {
                                downloadPDF(props.invoice.id).then((res) => {
                                    if (res?.result) {
                                        let pdfWindow = window.open("");
                                        pdfWindow!.document.write(`<iframe width='100%' height='100%' src='data:application/pdf;base64, ${encodeURI(res.result)}'></iframe>`);
                                    }
                                });
                            }}>
                        {t("preview.buttons.download")}
                    </Button>}

                {hasScope(CREATE_RECEIVABLE_VARIATION_NOTE) &&
                    !props.editable && props.invoice && (type === ID_INVOICE || type === ID_DEBIT_NOTE) && (props.invoice.status === ID_ACCOUNTED || props.invoice.status === ID_OPEN) &&
                    <Button color={"accent"} variant="contained" component="label"
                            onClick={() => navigate(`/finance/invoices/note-wizard/${props.invoice.id}`)}>
                        {t("preview.buttons.create-variation")}
                    </Button>

                }
                {hasScope(SUB_EXTRACANONE) &&
                    <>
                        {(showStatusesExtraCanoneChange || props.invoice.status === ID_CANCELLED) &&
                            <Button color={"accent"} variant="contained" component="label"
                                    onClick={() => updateInvoiceStatus(props.invoice.id, ID_TO_SEND)}>
                                {t("preview.buttons.change-status-to-send")}
                            </Button>}

                        {showStatusesExtraCanoneChange  &&
                            <Button color={"accent"} variant="contained" component="label"
                                    onClick={() => updateInvoiceStatus(props.invoice.id, ID_CANCELLED)}>
                                {t("preview.buttons.change-status-cancelled")}
                            </Button>}
                    </>

                }
            </Box>
            }
        </>
    )
}
export default InvoiceDetails;